import { CoachSettings, WebComponents, WebSettings } from "@/interfaces";
import { useAccountSettings } from "./useAccountSettings";
import { usePlayerSettings } from "./usePlayerSettings";
import { useSocialCoachSession } from "./useSocialCoachSession";

export interface DynamicPromptsAccess {
  loading: boolean;
  enabled: boolean;
  canView: boolean;
  canEdit: boolean;
  isReadOnly: boolean;
  canCreateReviewDesigns: boolean;
  canPostForPlayers: boolean;
  isAdmin: boolean;
}

/**
 * The single gate for Prompt Studio and every "Post for a player" entry point. Assistants need
 * prompt library WRITE to post for players. Super admins never post for a player; to act for an
 * account they impersonate it and post as that coach.
 */
export function useDynamicPromptsAccess(): DynamicPromptsAccess {
  const { isAdmin, isMainCoach, isAssistantCoach } = useSocialCoachSession();
  const {
    hasAccountSetting,
    hasReadPermission,
    hasWritePermission,
    accountSettingsLoading,
    accountPermissionsLoading,
  } = useAccountSettings();

  const loading = !isAdmin && (accountSettingsLoading || accountPermissionsLoading);
  const enabled = isAdmin || hasAccountSetting(WebSettings.DYNAMIC_PROMPTS);
  const canView = isAdmin || (enabled && hasReadPermission(WebComponents.DYNAMIC_PROMPTS));
  const canEdit = isAdmin || (enabled && hasWritePermission(WebComponents.DYNAMIC_PROMPTS));
  const isReadOnly = canView && !canEdit;
  const canCreateReviewDesigns = isAdmin || (canEdit && hasReadPermission(WebComponents.REPUTATION_MANAGEMENT));
  // Bedrock requires DYNAMIC_PROMPTS WRITE to render for a player, so posting hangs off canEdit rather
  // than the account setting alone.
  // Only coach sessions post for players: the coach, or an assistant with prompt library WRITE.
  const canPostForPlayers = canEdit && (isMainCoach || (isAssistantCoach && hasWritePermission(WebComponents.PROMPTS)));

  return { loading, enabled, canView, canEdit, isReadOnly, canCreateReviewDesigns, canPostForPlayers, isAdmin };
}

/** Undefined while the player's settings are loading. */
export function usePlayerDynamicPromptsAccess(playerId: string | number | null | undefined): boolean | undefined {
  const { hasSetting } = usePlayerSettings(playerId);
  return hasSetting(CoachSettings.PLAYER_DYNAMIC_PROMPTS);
}
